import { MODES } from 'darwin_core/utils/consts.js';
import {
  humanizeTemperature,
  makeDhwModeNameHumanized,
  makeModeNameHumanized,
} from 'darwin_core/utils/helpers.js';

// Pure derivation of a Property zone tile's display state, ported from the mobile
// Property screen's ZoneTile (app/src/screens/Property/Property.js) so the web tile
// reads the same way:
//   - a big, prominent state — the target temperature for a heating zone, the live
//     On/Off for a hot-water zone;
//   - a small state line — the mode, or Holiday / Offline;
//   - an "actively heating / hot water on" highlight (the app's accent border);
//   - and, like the app, no navigation while the zone's thermostat is in holiday.
//
// Inputs are the already-resolved primitives the darwin_core selectors return, so
// this stays free of the store and is unit-tested in isolation. It matches the
// app's choices exactly (e.g. humanizeTemperature's 500-centidegree OFF threshold,
// the cold-colour test against the zone's own minimum) rather than "improving"
// them, so the two clients read identically.
export function tileState({
  isDhw,
  online,
  inHoliday,
  mode,
  setpoint,
  minSetpoint,
  roomTemperature,
  dhwActiveMode,
  until,
}) {
  // Holiday blocks navigation (matching the app: a zone cannot be opened while its
  // thermostat is in holiday — per thermostat, so another zone's tile may still
  // open). Offline still opens — the zone screen shows the last-reported detail
  // and disables its controls.
  const navigable = !inHoliday;

  if (isDhw) {
    // Hot water. The small line only names the mode when it is a scheduling mode
    // (Auto / Boost); On/Off are already conveyed by the big state, so — as in the
    // app — the small line is left blank for them.
    let smallState = '';
    if (!online) {
      smallState = 'Offline';
    } else if (inHoliday) {
      smallState = 'Holiday';
    } else if (until) {
      // Following a schedule: name when the current state holds until (matches the
      // mockup's "Until 10:00am"); the caller only passes `until` when relevant.
      smallState = `Until ${until}`;
    } else if (mode !== MODES.On && mode !== MODES.Off) {
      smallState = makeDhwModeNameHumanized(mode);
    }

    return {
      kind: 'water',
      bigState: online ? dhwActiveMode : '',
      smallState,
      // Matches the app's showBorder (Property.js): an offline zone never shows the
      // active accent, even if its last-reported mode was On.
      active: online && dhwActiveMode === MODES.On,
      frost: false,
      navigable,
    };
  }

  // Heating (CH / HPI_CH).
  let smallState;
  if (!online) {
    smallState = 'Offline';
  } else if (inHoliday) {
    smallState = 'Holiday';
  } else if (until) {
    smallState = `Until ${until}`;
  } else {
    smallState = makeModeNameHumanized(mode);
  }

  const hasSetpoint = typeof setpoint === 'number';
  // humanizeTemperature(setpoint, showdegrees, showcelsius, showOff): renders "OFF"
  // at/below the 500-centidegree frost floor, else "21.0°c". Matches the app's tile.
  const bigState =
    online && hasSetpoint
      ? humanizeTemperature(setpoint, true, true, true)
      : '';

  return {
    kind: 'thermostat',
    bigState,
    smallState,
    // Calling for heat: the target is above the current room temperature.
    active:
      online &&
      hasSetpoint &&
      typeof roomTemperature === 'number' &&
      setpoint > roomTemperature,
    // At/below the zone's own minimum → show the big state in the cold colour.
    frost: online && hasSetpoint && setpoint <= minSetpoint,
    navigable,
  };
}

// The Property geolocation tile's display state, ported from the same mobile
// Property screen (its smallStateText + the showBorder rule on the geolocation
// Tile). `mode` is what darwin_core's geolocationHomeMode reports for the home:
// 'home' while any registered phone is at home, 'away' once every phone has left,
// and 'disabled' when geolocation is off or no phone is registered. Anything else
// reads as Disabled, as in the app's default branch. The tile has no big state.
export function geolocationTileState(mode) {
  let smallState;
  switch (mode) {
    case 'home':
      smallState = 'Home';
      break;
    case 'away':
      smallState = 'Away';
      break;
    default:
      smallState = 'Disabled';
  }
  return {
    smallState,
    // The app's accent border shows only while someone is home.
    active: mode === 'home',
  };
}
